import { useState } from "react";
import useSWR from "swr";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { IngestSources } from "@/components/ingesta/IngestSources";
import { IngestList } from "@/components/ingesta/IngestList";
import { ComparisonDialog, ProcessDialog } from "@/components/ingesta/ProcessDialog";
import { api, errMsg, fetcher } from "@/lib/api";

const PAGE = 30;
const EMPTY_MANUAL = { title: "", text: "", images: "", source_url: "" };

export default function Ingesta() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("pending");
  const [page, setPage] = useState(0);
  const { data: bandeja, mutate } = useSWR(
    `/ingest/items?limit=${PAGE}&skip=${page * PAGE}${filter === "all" ? "" : `&status=${filter}`}${query ? `&q=${encodeURIComponent(query)}` : ""}`,
    fetcher, { refreshInterval: 20000 });
  const items = bandeja?.items;
  const total = bandeja?.total || 0;
  const { data: outlets } = useSWR("/outlets", fetcher);
  const { data: models } = useSWR("/ai/models", fetcher);

  const [url, setUrl] = useState("");
  const [manual, setManual] = useState(EMPTY_MANUAL);
  const [blocked, setBlocked] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [progress, setProgress] = useState("");
  const [picked, setPicked] = useState([]);
  const [tab, setTab] = useState("enlace");
  const [files, setFiles] = useState([]);
  const [imageUrls, setImageUrls] = useState("");
  const [busy, setBusy] = useState("");
  const [target, setTarget] = useState(null);
  const [selected, setSelected] = useState([]);
  const [instructions, setInstructions] = useState("");
  const [model, setModel] = useState("");

  const run = async (key, fn, msg) => {
    setBusy(key);
    try {
      const res = await fn();
      toast.success(msg);
      mutate();
      return res;
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const addUrl = async () => {
    setBusy("url");
    setBlocked(null);
    try {
      await api.post("/ingest/url", { url });
      toast.success("Enlace procesado y añadido a la bandeja");
      setUrl("");
      mutate();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "blocked") setBlocked({ url: detail.url, message: detail.message });
      else toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const openPaste = () => {
    setManual({ ...EMPTY_MANUAL, source_url: blocked?.url || url });
    setTab("texto");
    setBlocked(null);
  };

  const addText = () =>
    run("text", async () => {
      await api.post("/ingest/text", {
        title: manual.title,
        text: manual.text,
        images: manual.images.split(",").map((s) => s.trim()).filter(Boolean),
        source_url: manual.source_url || "",
      });
      setManual(EMPTY_MANUAL);
    }, "Nota de prensa añadida");

  const addFiles = () =>
    run("files", async () => {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("image_urls", imageUrls);
      await api.post("/ingest/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFiles([]);
      setImageUrls("");
    }, "Archivos procesados");

  const scanEmail = () =>
    run("email", async () => {
      const { data } = await api.post("/ingest/scan-email");
      return data;
    }, "Bandeja de correo revisada");

  const bulk = async (body, confirmar = false) => {
    if (confirmar && !window.confirm("¿Eliminar definitivamente los elementos seleccionados?")) return;
    try {
      const { data } = await api.post("/ingest/items/bulk", body);
      toast.success(`${data.affected} elemento(s) ${data.action === "delete" ? "eliminados"
        : data.action === "restore" ? "restaurados" : "archivados"}`);
      setPicked([]);
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const archiveOld = async () => {
    const dias = window.prompt(
      "Archivar los pendientes con más de cuántos días de antigüedad?", "7");
    if (!dias) return;
    const n = Number(dias);
    if (!n || n < 1) return toast.error("Indique un número de días válido");
    if (!window.confirm(`Se archivarán todos los pendientes con más de ${n} día(s). ¿Continuar?`)) return;
    await bulk({ action: "archive", status: "pending", older_than_days: n });
  };

  const openProcess = (item) => {
    setTarget(item);
    setSelected(item.outlet_id ? [item.outlet_id] : []);
    setInstructions("");
    setModel(models?.default || "");
  };

  const deleteItem = (item) =>
    run("del", () => api.delete(`/ingest/items/${item.id}`), "Elemento eliminado");

  const waitForJob = async (jobId, label) => {
    for (let i = 0; i < 100; i += 1) {
      await new Promise((r) => setTimeout(r, 3000));
      const { data: job } = await api.get(`/ai-jobs/${jobId}`);
      if (job.state === "done") return job.result;
      if (job.state === "error") throw new Error(job.error || "La IA no pudo completar la tarea");
      setProgress(`${label} · ${(i + 1) * 3}s`);
    }
    throw new Error("La IA está tardando demasiado; vuelva a intentarlo");
  };

  const compareModels = async () => {
    if (!selected.length) return toast.error("Seleccione el medio con el que va a comparar");
    const elegido = model || models?.default || "claude-sonnet-4-6";
    const rival = (models?.models || [])
      .map((m) => m.id)
      .find((id) => id !== elegido &&
        (elegido.startsWith("gpt") ? !id.startsWith("gpt") : id.startsWith("gpt")))
      || "gpt-5.4-mini";
    setBusy("compare");
    setProgress("Redactando con los dos modelos…");
    try {
      const { data: start } = await api.post("/articles/compare-models", {
        item_id: target.id, outlet_id: selected[0], models: [elegido, rival], instructions,
      });
      const result = await waitForJob(start.job_id, "Comparando modelos");
      setComparison(result);
      (result.errors || []).forEach((e) => toast.error(`${e.model}: ${e.error}`));
    } catch (e) {
      toast.error(e.message || errMsg(e));
    } finally {
      setBusy("");
      setProgress("");
    }
  };

  const applyDraft = async (draftId) => {
    setBusy(`use-${draftId}`);
    try {
      const { data: article } = await api.post(`/articles/use-draft/${draftId}`);
      toast.success("Borrador creado con el modelo elegido");
      setComparison(null);
      setTarget(null);
      navigate(`/redaccion/${article.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const process = async () => {
    if (!selected.length) return toast.error("Seleccione al menos un medio");
    setBusy("process");
    try {
      setProgress("La IA está redactando…");
      const { data: start } = await api.post("/articles/process", {
        item_id: target.id,
        outlet_ids: selected,
        model: model || undefined,
        instructions,
      });
      const data = await waitForJob(start.job_id, "Redactando");
      toast.success(`${data.created} borrador(es) generado(s)`);
      (data.errors || []).forEach((e) => toast.error(`${e.outlet}: ${e.error}`));
      setTarget(null);
      mutate();
      if (data.articles?.[0]) navigate(`/redaccion/${data.articles[0].id}`);
    } catch (e) {
      toast.error(e.message || errMsg(e));
    } finally {
      setBusy("");
      setProgress("");
    }
  };

  return (
    <div>
      <PageHeader
        kicker="Recolección"
        title="Ingesta de notas de prensa"
        description="Enlaces, correos, mensajes de WhatsApp, documentos Word, PDF, HTML y texto plano con sus imágenes."
      >
        <Button variant="outline" onClick={scanEmail} disabled={busy === "email"} data-testid="scan-email-btn">
          <Mail className="h-4 w-4 mr-2" />
          {busy === "email" ? "Revisando…" : "Revisar correo"}
        </Button>
      </PageHeader>

      <IngestSources
        tab={tab} setTab={setTab} url={url} setUrl={setUrl} addUrl={addUrl} busy={busy}
        blocked={blocked} openPaste={openPaste}
        files={files} setFiles={setFiles} imageUrls={imageUrls} setImageUrls={setImageUrls}
        addFiles={addFiles} manual={manual} setManual={setManual} addText={addText}
      />

      <IngestList
        items={items} total={total} pageSize={PAGE} page={page} setPage={setPage}
        filter={filter} setFilter={setFilter} query={query} setQuery={setQuery}
        picked={picked} setPicked={setPicked} bulk={bulk} archiveOld={archiveOld}
        openProcess={openProcess} onDelete={deleteItem}
      />

      <ProcessDialog
        target={target} onClose={() => setTarget(null)} outlets={outlets} models={models}
        selected={selected} setSelected={setSelected} model={model} setModel={setModel}
        instructions={instructions} setInstructions={setInstructions}
        progress={progress} busy={busy} compareModels={compareModels} process={process}
      />

      <ComparisonDialog comparison={comparison} onClose={() => setComparison(null)}
                        busy={busy} applyDraft={applyDraft} />
    </div>
  );
}
